// Pure helpers for the Branch Cash reports page. The server re-validates every filter; these only stop an obviously bad request.

export const MAX_RANGE_DAYS = 93;
export type ReportKey = 'daily-summary' | 'movements' | 'sales-cash-reconciliation' | 'reversal-reviews';

export const REPORT_TABS: Array<{ key: ReportKey; label: string; needsRange: boolean; needsBranch: boolean }> = [
  { key: 'daily-summary', label: 'Daily cash summary', needsRange: true, needsBranch: false },
  { key: 'movements', label: 'Movement register', needsRange: true, needsBranch: false },
  { key: 'sales-cash-reconciliation', label: 'Sales cash reconciliation', needsRange: false, needsBranch: true },
  { key: 'reversal-reviews', label: 'Reversal reviews', needsRange: false, needsBranch: false },
];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const isRealDate = (s: string) => DATE_RE.test(s) && new Date(`${s}T00:00:00Z`).toISOString().slice(0, 10) === s;
const dayDiff = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

/** The last `days` calendar days ending on `today` (a YYYY-MM-DD string), inclusive. */
export function defaultRange(today: string, days = 7): { from: string; to: string } {
  const d = new Date(`${today}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return { from: d.toISOString().slice(0, 10), to: today };
}

/** '' when fine, otherwise the plain reason the range is refused (mirrors the server). */
export function rangeError(from: string, to: string): string {
  if (!isRealDate(from) || !isRealDate(to)) return 'Pick a valid From and To date';
  if (from > to) return 'From must not be after To';
  if (dayDiff(from, to) + 1 > MAX_RANGE_DAYS) return `Choose at most ${MAX_RANGE_DAYS} days`;
  return '';
}

export function reportParams(key: ReportKey, f: { from: string; to: string; branchId: string; type?: string; direction?: string; late?: string; status?: string }, extra: Record<string, string | number> = {}): Record<string, string | number> {
  const tab = REPORT_TABS.find((t) => t.key === key)!;
  const p: Record<string, string | number> = { ...extra };
  if (tab.needsRange) { p.from = f.from; p.to = f.to; }
  if (f.branchId) p.branchId = f.branchId;
  if (key === 'movements') {
    if (f.type) p.type = f.type;
    if (f.direction) p.direction = f.direction;
    if (f.late) p.late = f.late;
  }
  if (key === 'reversal-reviews' && f.status) p.status = f.status;
  return p;
}

/** A reason the chosen report cannot run yet ('' = it can). */
export function reportBlocker(key: ReportKey, f: { from: string; to: string; branchId: string }): string {
  const tab = REPORT_TABS.find((t) => t.key === key)!;
  if (tab.needsRange) { const e = rangeError(f.from, f.to); if (e) return e; }
  if (tab.needsBranch && !f.branchId) return 'Choose a branch';
  return '';
}

export const rupees = (n: number | null | undefined): string =>
  n === null || n === undefined ? '-' : `${n < 0 ? '-' : ''}₹${Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The file name a downloaded report gets (the server also names it; this is the fallback). */
export const reportFileName = (key: ReportKey, f: { from: string; to: string; branchId: string }): string =>
  `branch-cash-${key}${f.branchId ? '' : '-all-branches'}${REPORT_TABS.find((t) => t.key === key)!.needsRange ? `-${f.from}_${f.to}` : ''}.csv`;

export interface LinkedMovementLike { movementType: string; sourceRef: string | null; amount: number; businessDate: string }
/** "SALES_CASH_IN RCPT-1, 500.00, 2026-12-01" - the ledger row a reversal / adjustment / review points back to. */
export const linkedSummary = (l: LinkedMovementLike): string => `${l.movementType}${l.sourceRef ? ` ${l.sourceRef}` : ''}, ${rupees(l.amount)}, ${l.businessDate}`;
/** What a movement-register row is linked to ('' for an ordinary row). */
export function linkedNote(m: { reverses?: LinkedMovementLike | null; adjusts?: LinkedMovementLike | null; reversesMovementId?: string | null; adjustsMovementId?: string | null }): string {
  if (m.reversesMovementId) return `Reverses ${m.reverses ? linkedSummary(m.reverses) : 'an earlier entry'}`;
  if (m.adjustsMovementId) return `Adjusts ${m.adjusts ? linkedSummary(m.adjusts) : 'an earlier entry'}`;
  return '';
}

export const summaryRowNote = (r: { openingUnverified: boolean }): string => (r.openingUnverified ? 'Opening not verified - expected closing not shown' : '');
